/**
 * Strategy-article content module.
 *
 * Articles are typed JSX (two files per article: `<slug>.meta.js` holds the
 * frontmatter as plain fields, `<slug>.jsx` the Body component built from the
 * Prose primitives) — deliberately NOT MDX, which CRA can't load without
 * ejecting. Everything is accessed ONLY through the functions below, so a
 * future DB-backed CMS can replace this one file without touching any page.
 *
 * Only the frontmatter is imported here. Bodies are the bulk of the content
 * and one is read at a time, so `getArticle` hands out `loadBody()`, a dynamic
 * import, and a listing (landing band, UserPage highlights, strategy index)
 * puts no article prose in the initial bundle.
 *
 * Article shape: { slug, title, category, excerpt, readMinutes, date, author?, loadBody }.
 */
import draftByTiers from './draft-by-tiers.meta';
import waiverPriorityVsFaab from './waiver-priority-vs-faab.meta';
import readingTradeValue from './reading-trade-value.meta';
import streamingDefenseAndKicker from './streaming-defense-and-kicker.meta';
import playoffPrep from './playoff-prep.meta';
import preseasonWeek1Recap from './preseason-week-1-recap.meta';
import preseasonWeek2Recap from './preseason-week-2-recap.meta';
import preseasonWeek3Recap from './preseason-week-3-recap.meta';
import rookieDraftRoundGuide from './rookie-draft-round-guide.meta';
import patriotsSeahawksWeek1Preview from './patriots-seahawks-week1-preview.meta';
import rams49ersWeek1Preview from './rams-49ers-week1-preview.meta';
import week1SundayMondayPreview from './week1-sunday-monday-preview.meta';
import week2WaiverWirePriorityBoard from './week2-waiver-wire-priority-board.meta';
import week3WaiverWireDarknessReport from './week3-waiver-wire-darkness-report.meta';
import week3StartSitDarknessReport from './week3-start-sit-darkness-report.meta';
import week4WaiverWireDarknessReport from './week4-waiver-wire-darkness-report.meta';
import week4StartSitDarknessReport from './week4-start-sit-darkness-report.meta';
import week5WaiverWireDarknessReport from './week5-waiver-wire-darkness-report.meta';
import week5StartSitDarknessReport from './week5-start-sit-darkness-report.meta';
import week6WaiverWireDarknessReport from './week6-waiver-wire-darkness-report.meta';
import week7WaiverWireDarknessReport from './week7-waiver-wire-darkness-report.meta';
import week8WaiverWireDarknessReport from './week8-waiver-wire-darkness-report.meta';
import week9WaiverWireDarknessReport from './week9-waiver-wire-darkness-report.meta';
import week6StartSitDarknessReport from './week6-start-sit-darkness-report.meta';
import week7StartSitDarknessReport from './week7-start-sit-darkness-report.meta';
import week8StartSitDarknessReport from './week8-start-sit-darkness-report.meta';

// One loader per article, keyed by slug. Literal import() calls so the
// bundler can split one chunk per body.
const BODY_LOADERS = {
  'draft-by-tiers': () => import('./draft-by-tiers'),
  'waiver-priority-vs-faab': () => import('./waiver-priority-vs-faab'),
  'reading-trade-value': () => import('./reading-trade-value'),
  'streaming-defense-and-kicker': () => import('./streaming-defense-and-kicker'),
  'playoff-prep': () => import('./playoff-prep'),
  'preseason-week-1-recap': () => import('./preseason-week-1-recap'),
  'preseason-week-2-recap': () => import('./preseason-week-2-recap'),
  'preseason-week-3-recap': () => import('./preseason-week-3-recap'),
  'rookie-draft-round-guide': () => import('./rookie-draft-round-guide'),
  'patriots-seahawks-week1-preview': () => import('./patriots-seahawks-week1-preview'),
  'rams-49ers-week1-preview': () => import('./rams-49ers-week1-preview'),
  'week1-sunday-monday-preview': () => import('./week1-sunday-monday-preview'),
  'week2-waiver-wire-priority-board': () => import('./week2-waiver-wire-priority-board'),
  'week3-waiver-wire-darkness-report': () => import('./week3-waiver-wire-darkness-report'),
  'week3-start-sit-darkness-report': () => import('./week3-start-sit-darkness-report'),
  'week4-waiver-wire-darkness-report': () => import('./week4-waiver-wire-darkness-report'),
  'week4-start-sit-darkness-report': () => import('./week4-start-sit-darkness-report'),
  'week5-waiver-wire-darkness-report': () => import('./week5-waiver-wire-darkness-report'),
  'week5-start-sit-darkness-report': () => import('./week5-start-sit-darkness-report'),
  'week6-waiver-wire-darkness-report': () => import('./week6-waiver-wire-darkness-report'),
  'week7-waiver-wire-darkness-report': () => import('./week7-waiver-wire-darkness-report'),
  'week8-waiver-wire-darkness-report': () => import('./week8-waiver-wire-darkness-report'),
  'week9-waiver-wire-darkness-report': () => import('./week9-waiver-wire-darkness-report'),
  'week6-start-sit-darkness-report': () => import('./week6-start-sit-darkness-report'),
  'week7-start-sit-darkness-report': () => import('./week7-start-sit-darkness-report'),
  'week8-start-sit-darkness-report': () => import('./week8-start-sit-darkness-report'),
};

// Newest first.
const ARTICLES = [
  draftByTiers,
  waiverPriorityVsFaab,
  readingTradeValue,
  streamingDefenseAndKicker,
  playoffPrep,
  preseasonWeek1Recap,
  preseasonWeek2Recap,
  preseasonWeek3Recap,
  rookieDraftRoundGuide,
  patriotsSeahawksWeek1Preview,
  rams49ersWeek1Preview,
  week1SundayMondayPreview,
  week2WaiverWirePriorityBoard,
  week3WaiverWireDarknessReport,
  week3StartSitDarknessReport,
  week4WaiverWireDarknessReport,
  week4StartSitDarknessReport,
  week5WaiverWireDarknessReport,
  week5StartSitDarknessReport,
  week6WaiverWireDarknessReport,
  week7WaiverWireDarknessReport,
  week8WaiverWireDarknessReport,
  week9WaiverWireDarknessReport,
  week6StartSitDarknessReport,
  week7StartSitDarknessReport,
  week8StartSitDarknessReport,
].sort((a, b) => new Date(b.date) - new Date(a.date));

const META_KEYS = ['slug', 'title', 'category', 'excerpt', 'readMinutes', 'date', 'author'];

/** Metadata-only view (no Body) for cards and listings. */
function toMeta(article) {
  const meta = {};
  for (const key of META_KEYS) meta[key] = article[key];
  return meta;
}

/** All articles as metadata, newest first. */
export function listArticles() {
  return ARTICLES.map(toMeta);
}

/**
 * The article for a slug, or null: its metadata plus `loadBody()`, which
 * resolves to the Body component (a dynamic import; call it when the prose
 * is about to be shown, typically through React.lazy).
 */
export function getArticle(slug) {
  const meta = ARTICLES.find((a) => a.slug === slug);
  if (!meta) return null;
  const loader = BODY_LOADERS[slug];
  return {
    ...meta,
    loadBody: () => (loader
      ? loader().then((mod) => mod.default)
      : Promise.reject(new Error(`content/articles: no body loader registered for "${slug}" (add it to BODY_LOADERS)`))),
  };
}

/** First `n` articles as metadata (for the landing teaser + featured slots). */
export function featuredArticles(n = 3) {
  return listArticles().slice(0, n);
}

/** Up to `n` other articles, same category first, for a "Related" strip. */
export function relatedArticles(slug, n = 3) {
  const current = getArticle(slug);
  if (!current) return [];
  const clusters = {
    Waivers: ['Waivers', 'Streaming', 'Playoffs'],
    Streaming: ['Streaming', 'Waivers', 'Playoffs'],
    Playoffs: ['Playoffs', 'Streaming', 'Waivers'],
  };
  const preferred = clusters[current.category] || [current.category];
  const others = ARTICLES.filter((a) => a.slug !== slug);
  others.sort((a, b) => {
    const aIndex = preferred.indexOf(a.category);
    const bIndex = preferred.indexOf(b.category);
    const aPriority = aIndex === -1 ? preferred.length : aIndex;
    const bPriority = bIndex === -1 ? preferred.length : bIndex;
    return aPriority - bPriority;
  });
  return others.slice(0, n).map(toMeta);
}
